import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback } from 'react';
import { useMeContext } from '~/modules/me';
import {
  useResourceUsers,
  useResourceAllocationChartEventHandlers
} from '~/modules/resourcing/common/hooks';
import {
  useKeyValueSettingsMutation,
  updateCache
} from '~/modules/common/hooks';
import { ResourceLegendComponent } from '~/modules/common/components/ResourceLegendComponent';
import DisplayUnitContextProvider from '~/modules/resourcing/common/hooks/useDisplayUnitContext';
import useFilterBasedOnSearchCriteria from '~/modules/resourcing/hooks/useFilterBasedOnSearchCriteria';
import { getTodayForUser } from '~/modules/common/dates/today';
import {
  ResourceAllocationUserChartLoading,
  ResourceAllocationUserChartHeaderRow,
  ResourceAllocationUserChartToolbar,
  NoResourceOrPageOutOfBoundMessage,
  ResourceAllocationUserRow
} from './components';
import { ALLOCATION_CHART_SETTINGS } from './constants';

const LegendComponent = <ResourceLegendComponent />;
const PAGE_SIZE = 20;

export const ResourceAllocationUserChartContent = ({
  leftComponentWidth,
  chartDates,
  onPrevious,
  onNext,
  scale,
  setScale,
  setDate,
  dateRange,
  defaultDisplayUnit,
  searchCriteria,
  currentPageForTimeline,
  setCurrentPageForTimeline
}) => {
  const scaleValue = useRef(scale);
  const [displayUnit, setDisplayUnit] = useState(defaultDisplayUnit);
  const [putKeyValueSettings] = useKeyValueSettingsMutation(
    updateCache(ALLOCATION_CHART_SETTINGS)
  );

  const {
    handleDisplayUnitChange,
    handleScaleChange
  } = useResourceAllocationChartEventHandlers({
    key: ALLOCATION_CHART_SETTINGS,
    setScale,
    scale,
    putKeyValueSettings,
    displayUnit,
    setDisplayUnit
  });

  const me = useMeContext();

  const {
    permissionsMap,
    featureFlags: { isRmp2025Q4Enhancements, isRmp2026Q1Enhancements }
  } = me;

  const hasViewProjectRolePermission = Boolean(
    permissionsMap['urn:replicon:user-action:view-project-role']
  );

  const today = getTodayForUser(me);

  const { filter } = useFilterBasedOnSearchCriteria({ searchCriteria });
  const { loadingRows, loadingMore, resourceUsers } = useResourceUsers({
    page: currentPageForTimeline,
    pagesize: PAGE_SIZE,
    filter,
    dateRange,
    scale,
    isViewAvailabilityEnabled: Boolean(
      permissionsMap['urn:replicon:user-action:view-availability']
    ),
    isViewResourceAllocation: false,
    includeRoleScheduleAsOfDate:
      isRmp2025Q4Enhancements && hasViewProjectRolePermission,
    asOfDate: today,
    skipMerge: true
  });

  const handleTimelineScaleChange = useCallback(
    value => {
      setCurrentPageForTimeline(1);
      handleScaleChange(value);
    },
    [handleScaleChange, setCurrentPageForTimeline]
  );

  return (
    <DisplayUnitContextProvider
      defaultDisplayUnit={displayUnit}
      onDisplayUnitChange={handleDisplayUnitChange}
    >
      <ResourceAllocationUserChartToolbar
        scale={scale}
        dateRange={dateRange}
        onPreviousClick={onPrevious}
        onNextClick={onNext}
        setScale={handleTimelineScaleChange}
        setDate={setDate}
        LegendComponent={LegendComponent}
        displayDialog={false}
        showDateRange
      />
      <ResourceAllocationUserChartHeaderRow
        resourceTotalCount={resourceUsers?.length || 0}
        currentPage={currentPageForTimeline}
        dateRange={dateRange}
        pageSize={PAGE_SIZE}
        setCurrentPage={setCurrentPageForTimeline}
        loadingRows={loadingRows}
        scale={scale}
        leftComponentWidth={leftComponentWidth}
        chartDates={chartDates}
        pageUsers={resourceUsers}
        showTableHeaderNavigation
      />
      {!loadingRows &&
      scaleValue.current === scale &&
      resourceUsers.length > 0 ? (
        resourceUsers.map(u => (
          <ResourceAllocationUserRow
            key={`resource_user${u.id}`}
            scale={scale}
            resource={u}
            leftComponentWidth={leftComponentWidth}
            chartDisplayDateRange={dateRange}
            chartDates={chartDates}
            isRmp2025Q4Enhancements={isRmp2025Q4Enhancements}
            isRmp2026Q1Enhancements={isRmp2026Q1Enhancements}
          />
        ))
      ) : (
        <NoResourceOrPageOutOfBoundMessage
          loadingRows={loadingRows}
          currentPage={currentPageForTimeline}
          setCurrentPage={setCurrentPageForTimeline}
          resourceTotalCount={resourceUsers?.length || 0}
        />
      )}
      {(loadingRows || loadingMore) && <ResourceAllocationUserChartLoading />}
    </DisplayUnitContextProvider>
  );
};

ResourceAllocationUserChartContent.propTypes = {
  leftComponentWidth: PropTypes.number.isRequired,
  chartDates: PropTypes.array,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  scale: PropTypes.any,
  setScale: PropTypes.func,
  setDate: PropTypes.func,
  dateRange: PropTypes.object,
  searchCriteria: PropTypes.object,
  defaultDisplayUnit: PropTypes.string,
  currentPageForTimeline: PropTypes.number,
  setCurrentPageForTimeline: PropTypes.any
};

export default ResourceAllocationUserChartContent;
